import { useHistory } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import cogoToast from 'cogo-toast';
import { Container } from 'reactstrap';

import Table from './components/Tables/Table';
import Filter from './components/Filters/Filter';
import QuestionModal from './components/Modals/Question';
import { Constant } from '../../../../../Helpers/constant';
import { BreadCrumb, Loader } from '../../../../../Common';
import UpdateBusiness from './components/Modals/UpdateBusiness';
import QuestionAnswerModal from './components/Modals/QuestionAnswer';
import {
  DOWNLOAD_ADIB_BOOKING,
  DOWNLOAD_ADIB_BOOKING_EMPTY,
  GET_BUSINESS,
  GET_BUSINESS_EMPTY,
  GET_QUES_ANS,
  GET_QUES_ANS_EMPTY,
} from '../../../../../../store/actions';
import UpdateSetting from './components/Modals/UpdateSetting';

// Define the shape of the filter object
interface FilterState {
  createdAtRange: {
    from: string;
    to: string;
  };
}

// Define the shape of your Redux state
interface RootState {
  GetBusiness: {
    data: any;
    loading: boolean;
    error: any;
  };
}

const BusinessRegistration: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  /* ---------------------------- LOCAL STATES ---------------------------- */
  const [loading, setLoading] = useState<boolean>(false);
  const [currentlyAppliedFilters, setCurrentlyAppliedFilters] = useState(null);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [currentSelectedCompanyDetails, setCurrentSelectedCompanyDetails] = useState(null);
  const [isUpdateModalShow, setIsUpdateModalShow] = useState(false);
  const [isQuestionModalShow, setIsQuestionModalShow] = useState(false);
  const [isQuestionWithAnsModalShow, setIsQuestionWithAnsModalShow] = useState(false);
  const [isUserSettingModalShow, setIsUserSettingModalShow] = useState(false);
  const [updateType, setUpdateType] = useState('');
  const [actionType, setActionType] = useState('');

  /* ---------------------------- REDUX STATES ---------------------------- */
  // Get Business
  const { getBusiness, getBusinessLoading } = useSelector((state: RootState) => ({
    getBusiness: state?.GetBusiness?.data?.data,
    getBusinessLoading: state?.GetBusiness?.loading,
  }));

  // Update Business
  const { updateBusiness, updateBusinessLoading } = useSelector((state: any) => ({
    updateBusiness: state?.UpdateBusiness?.data,
    updateBusinessLoading: state?.UpdateBusiness?.loading,
  }));

  // Ask Question
  const { askQuestionLoading } = useSelector((state: any) => ({
    askQuestionLoading: state?.AskQuestion?.loading,
  }));

  // Get Questions With Ans
  const { getQuesAns, getQuesAnsLoading } = useSelector((state: any) => ({
    getQuesAns: state?.GetQuestionAnswer?.data,
    getQuesAnsLoading: state?.GetQuestionAnswer?.loading,
  }));

  // Click Download Dump
  const { downloadDump, downloadDumpLoading } = useSelector((state: any) => ({
    downloadDump: state?.DownloadAdibBookingDump?.data,
    downloadDumpLoading: state?.DownloadAdibBookingDump?.loading,
  }));

  const { updateUserSetting, updateUserSettingLoading } = useSelector((state: any) => ({
    updateUserSetting: state?.UpdateUserSetting?.data,
    updateUserSettingLoading: state?.UpdateUserSetting?.loading,
  }));

  /* ---------------------------- DISPATCHING ---------------------------- */
  useEffect(() => {
    return () => {
      dispatch({
        type: GET_BUSINESS_EMPTY,
      });
    };
  }, [dispatch]);

  useEffect(() => {
    if (updateBusiness !== null || updateUserSetting !== null) {
      dispatch({
        type: GET_BUSINESS,
        payload: {
          urlParam: {
            pageIndex: currentPageNumber * Constant.TABLE_PAGE_SIZE,
          },
          data: null,
        },
      });
    }
  }, [updateBusiness, dispatch, currentPageNumber, updateUserSetting]);

  useEffect(() => {
    dispatch({
      type: GET_BUSINESS,
      payload: {
        urlParam: {
          pageIndex: currentPageNumber * Constant.TABLE_PAGE_SIZE,
        },
        data: null,
      },
    });
  }, [dispatch]);

  useEffect(() => {
    if (getQuesAns && actionType === 'QUESTION_ASKED') {
      getQuesAns?.data?.length < 2 ? setIsQuestionModalShow(true) : cogoToast.warn('You can not ask more than two questions');
    }
    dispatch({
      type: GET_QUES_ANS_EMPTY,
    });
  }, [getQuesAns]);

  useEffect(() => {
    if (downloadDump !== null) {
      dispatch({
        type: DOWNLOAD_ADIB_BOOKING_EMPTY,
      });
    }
  }, [downloadDump]);

  /* ---------------------------- TABLE ACTION HANDLER ---------------------------- */
  const tableActionHandler = params => {
    setCurrentSelectedCompanyDetails(params?.companyDetails);
    setActionType(params?.actionType);

    switch (params?.actionType) {
      case 'ACCEPT':
        setIsUpdateModalShow(true);
        setUpdateType('Accept');
        break;

      case 'REJECT':
        setIsUpdateModalShow(true);
        setUpdateType('Reject');
        break;

      case 'BLOCK':
        setIsUpdateModalShow(true);
        setUpdateType('Block');
        break;

      case 'UNBLOCK':
        setIsUpdateModalShow(true);
        setUpdateType('Unblock');
        break;

      case 'QUESTION_ASKED':
        dispatch({
          type: GET_QUES_ANS,
          payload: {
            data: {
              businessId: params?.companyDetails?.id,
              type: 1,
            },
          },
        });
        break;

      case 'QUES_ANS':
        setIsQuestionWithAnsModalShow(true);
        break;

      case 'POST_ACTIVE':
        setIsUserSettingModalShow(true);
        break;

      case 'POST_INACTIVE':
        setIsUserSettingModalShow(true);
        break;

      default:
        break;
    }
  };

  /* ---------------------------- DOWNLOAD DUMP HANDLER ---------------------------- */
  const downloadDumpHandler = () => {
    const user = JSON.parse(localStorage.getItem('authUser'));
    dispatch({
      type: DOWNLOAD_ADIB_BOOKING,
      payload: {
        data: {
          adminId: user?.data?.userId,
          projectCode: 'ADIB_V2',
          downloadType: 11,
          pageIndex: 0,
          pageSize: Number(getBusiness?.count),
          ...currentlyAppliedFilters,
        },
      },
    });
  };

  /* ---------------------------- FILTER SUBMIT HANDLER ---------------------------- */
  const filterSubmitHandler = (filters: FilterState) => {
    if (filters) {
      setCurrentPageNumber(0);
      setCurrentlyAppliedFilters(filters);
      dispatch({
        type: GET_BUSINESS,
        payload: {
          urlParam: {
            pageIndex: 0,
          },
          data: {
            ...filters,
          },
        },
      });
    }
  };

  /* ---------------------------- PAGINATION HANDLER ---------------------------- */
  const paginationHandler = (pageNumber: number) => {
    setCurrentPageNumber(pageNumber);
    dispatch({
      type: GET_BUSINESS,
      payload: {
        urlParam: {
          pageIndex: pageNumber * Constant.TABLE_PAGE_SIZE,
          sortOn: currentlyAppliedFilters?.sortOn ?? 'updatedAt',
        },
        data: currentlyAppliedFilters,
      },
    });
  };

  /* ---------------------------- BREADCRUMB HANDLER ---------------------------- */
  const breadcrumNavigationHandler = title => {
    if (title === `Dashboard`) {
      history.push(`/client/dashboard`);
    }
  };

  /* ---------------------------- LOADING HANDLER ---------------------------- */
  useEffect(() => {
    setLoading(
      getBusinessLoading || updateBusinessLoading || askQuestionLoading || getQuesAnsLoading || downloadDumpLoading || updateUserSettingLoading,
    );
  }, [getBusinessLoading, updateBusinessLoading, askQuestionLoading, getQuesAnsLoading, downloadDumpLoading, updateUserSettingLoading]);

  return (
    <>
      {loading && <Loader zIndex={true} />}

      {isUpdateModalShow && (
        <UpdateBusiness data={currentSelectedCompanyDetails} onCloseHandler={() => setIsUpdateModalShow(false)} updateType={updateType} />
      )}

      {isUserSettingModalShow && <UpdateSetting data={currentSelectedCompanyDetails} onCloseHandler={() => setIsUserSettingModalShow(false)} />}

      {isQuestionModalShow && <QuestionModal data={currentSelectedCompanyDetails} onCloseHandler={() => setIsQuestionModalShow(false)} />}

      {isQuestionWithAnsModalShow && (
        <QuestionAnswerModal data={currentSelectedCompanyDetails} onCloseHandler={() => setIsQuestionWithAnsModalShow(false)} />
      )}

      <div className="page-content">
        <Container fluid className="position-relative">
          <>
            <BreadCrumb title="Business Registration" navigationby={breadcrumNavigationHandler} navigation={[`Dashboard`, `Business Registration`]} />
            <Filter filterHandler={filterSubmitHandler} appliedFilters={currentlyAppliedFilters} />
            <Table
              data={getBusiness?.data}
              totalData={getBusiness?.count}
              pageNumber={currentPageNumber}
              pageHandler={paginationHandler}
              actionHandler={tableActionHandler}
              dumpDownload={downloadDumpHandler}
              appliedFilters={currentlyAppliedFilters}
            />
          </>
        </Container>
      </div>
    </>
  );
};

export default BusinessRegistration;
